import {
  ImageScaleView,
  View,
  ViewOpts,
  animate,
} from '@play-co/timestep-core/ui';
import bitmapFonts from 'src/lib/bitmapFonts';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import uiConfig from 'src/lib/ui/config';
import i18n from 'src/lib/i18n/i18n';
import {
  BundleSaleProductId,
  bundleSaleRewards,
} from 'src/replicant/match2/bundlesale/match2-bundlesale.ruleset';
import { Match2BundleSaleIcon } from './Match2BundleSaleIcon';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { now, user } from 'src/StateObserver';
import {
  match2BundleSaleGetCurrentEventItems,
  match2BundleSaleGetPurchasedItems,
} from 'src/replicant/match2/bundlesale/match2-bundlesale.getters';
import { match2BundleSaleGetProductPriceStr } from './match2-bundlesale.utils';
import { processMatch2Rewards } from 'src/replicant/match2/rewards/match2-rewards.getters';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';

export type Match2BundleSalePackParams = {
  productId: BundleSaleProductId;
  onPurchase: (productId: BundleSaleProductId) => void;
  arrowDirection?: number;
};

export class Match2BundleSalePack extends View {
  public readonly bg: ImageScaleView;
  public readonly check: ImageScaleView;
  public readonly lock: ImageScaleView;
  public button: ButtonScaleViewWithText;
  public productId!: BundleSaleProductId;
  public rewards: FlexBoxView;
  public onPurchase!: (productId: BundleSaleProductId) => void;
  public debugLabel?: LangBitmapFontTextView;
  public baseScale = 1;
  public container: View; // For animation purposes

  constructor(opts: ViewOpts) {
    super({
      width: 258,
      height: 272,
      centerOnOrigin: true,
      ...opts,
    });

    this.container = new View({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      centerOnOrigin: true,
      x: this.style.width * 0.5,
      y: this.style.height * 0.5,
    });

    this.baseScale = this.style.scale || 1;

    this.bg = new ImageScaleView({
      superview: this.container,
      width: this.style.width,
      height: this.style.height,
      image: 'assets/ui/match2/bundlesale/bundlesale_card_normal.png',
      scaleMethod: 'contain',
    });

    this.rewards = new FlexBoxView({
      superview: this.container,
      x: 10,
      y: 10,
      width: this.style.width - 20,
      height: this.style.height * 0.5,
      flex: {
        flexDirection: 'row',
        alignContent: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
      },
      canHandleEvents: false,
    });

    this.check = new ImageScaleView({
      superview: this.container,
      x: this.style.width - 55,
      y: -8,
      width: 63,
      height: 64,
      image: 'assets/ui/match2/bundlesale/bundlesale_check.png',
      scaleMethod: 'contain',
    });

    this.button = new ButtonScaleViewWithText({
      superview: this.container,
      tag: 'exchangeBtn',
      ...uiConfig.buttons.primary,
      image: 'assets/ui/match2/bundlesale/bundlesale_button_green.png',
      imgScale: 0.66,
      textColor: '#ffffff',
      fontSize: 26,
      labelOffsetX: -3,
      labelOffsetY: -1,
      font: bitmapFonts('Body'),
      width: 149,
      height: 65,
      x: 54,
      y: 165,
      pressedOffsetY: 2,
      disabledFontOffsetY: -1,
      localeText: () => i18n('currencyExchange.gemsShop.exchangeButton'),
      onClick: async () => this.onPurchase(this.productId),
      tracking: { feature: 'bundle-sale', $subFeature: 'bundle-sale-purchase' },
    });

    this.lock = new ImageScaleView({
      superview: this.container,
      x: this.button.style.x + this.button.style.width - 20,
      y: this.button.style.y - 20,
      width: 50,
      height: 59,
      image: 'assets/ui/match2/bundlesale/bundlesale_lock.png',
      scaleMethod: 'contain',
    });

    if (process.env.IS_DEVELOPMENT) {
      // Set up a debug label to show the product id, in development mode
      this.debugLabel = new LangBitmapFontTextView({
        superview: this.container,
        x: 15,
        y: 260,
        width: this.style.width - 30,
        height: 10,
        font: bitmapFonts('Body'),
        color: 'white',
        align: 'center',
        verticalAlign: 'center',
        size: 16,
        opacity: 0.3,
      });
    }
  }

  public setup(params: Match2BundleSalePackParams) {
    this.productId = params.productId;
    if (this.debugLabel) {
      this.debugLabel.localeText = () => this.productId;
    }
    this.onPurchase = params.onPurchase;
    this.updateRewards();
    this.refresh();
  }

  private updateRewards() {
    const pack = bundleSaleRewards[this.productId];
    const processed = processMatch2Rewards(pack.rewards, now());

    let scale;
    if (pack.rewards.length === 1) {
      scale = 0.65;
    } else if (pack.rewards.length === 2) {
      scale = 0.55;
    } else {
      scale = 0.5;
    }

    for (const reward of processed) {
      const icon = new Match2BundleSaleIcon({});
      icon.setup(reward, scale);
      this.rewards.addSubview(icon);
    }
  }

  public refresh() {
    // Get all items available of the current event
    const items = match2BundleSaleGetCurrentEventItems(user(), now());

    // Get all items already purchased, may include items from previous events
    const allPurchases = match2BundleSaleGetPurchasedItems(user(), now());

    // Filter out eventual items from previous events
    const currentPurchases = allPurchases.filter((id) => items.includes(id));

    // If the product is already purchased, set as claimed
    if (currentPurchases.includes(this.productId)) {
      this.setAsClaimed();
      return;
    }

    // Find the index of the product in the list of available items
    const index = items.indexOf(this.productId);

    // Products are unlocked in sequence, so only the first unpurchased product should be available
    if (index <= currentPurchases.length) {
      this.setAsAvailable();
    } else {
      this.setAsLocked();
    }
  }

  private setAsLocked() {
    this.bg.updateOpts({
      image: 'assets/ui/match2/bundlesale/bundlesale_card_normal.png',
    });
    this.lock.show();
    this.check.hide();
    this.button.setDisabled(true);
    // this.button.localeText = () => i18n('match2.bundleSale.free');
    this.button.localeText = () =>
      match2BundleSaleGetProductPriceStr(this.productId);
  }

  private setAsAvailable() {
    this.bg.updateOpts({
      image: 'assets/ui/match2/bundlesale/bundlesale_card_current.png',
    });
    this.lock.hide();
    this.check.hide();
    this.button.setDisabled(false);
    this.button.localeText = () =>
      match2BundleSaleGetProductPriceStr(this.productId);
  }

  private setAsClaimed() {
    this.bg.updateOpts({
      image: 'assets/ui/match2/bundlesale/bundlesale_card_normal.png',
    });
    this.lock.hide();
    this.check.show();
    this.button.setDisabled(true);
    this.button.localeText = () => i18n('match2.bundleSale.claimed');
  }

  public async animateShow() {
    this.updateOpts({ scale: 0 });
    await animate(this)
      .now({ scale: this.baseScale }, 300, animate.easeOutBack)
      .toPromise();
  }

  public async animateHide() {
    await animate(this).now({ scale: 0 }, 300, animate.easeInBack).toPromise();
  }

  public async animateMove(position: { x: number; y: number }) {
    await animate(this).now(position, 300, animate.easeInOut).toPromise();
  }

  public async animateWobble() {
    await animate(this.container)
      .now({ scale: 1.2, r: -0.1 }, 100, animate.easeOut)
      .then({ r: 0.1 }, 100, animate.easeOut)
      .then({ r: -0.1 }, 100, animate.easeOut)
      .then({ r: 0.1 }, 100, animate.easeOut)
      .then({ scale: 1, r: 0 }, 300, animate.easeOutBack)
      .toPromise();
  }
}
